/**
 * Client lib for the TNK-130 staged import flow. Wraps /api/imports
 * endpoints so the wizard route file stays focused on UI state.
 *
 * The wizard parses the CSV client-side (re-using the existing
 * csvParser.ts helpers), normalizes each row into a `parsed` payload,
 * and posts both the original csvRow + the parsed normalization. The
 * server persists both so a future re-parse can happen without
 * re-uploading the file.
 */
import { apiFetch } from "@/lib/apiClient";

export interface ApiImportDraft {
  id: string;
  /** 'csv' (default) or 'scan' (TNK-194) — the review page branches on this to
   *  render scan drafts (vision read instead of CSV columns). */
  kind: string;
  sourceFormat: string;
  sourceFilename?: string;
  totalRows: number;
  status: string;
  batchBinId?: string;
  batchLotId?: string;
  createdAt: string;
  updatedAt: string;
  committedAt?: string;
  /** TNK-133 phase D — set when this committed draft has been rolled
   *  back via POST /imports/:id/rollback. The results page renders a
   *  dim "Rolled back" banner in place of the success header when set. */
  rolledBackAt?: string;
  /** TNK-130 follow-up: per-status item totals returned by the list
   *  endpoint so the row can show "240 done · 9 failed" without a
   *  second round-trip per draft. Older API versions may omit it. */
  counters?: DraftCounters;
}

/**
 * TNK-141 — server-resolved catalog candidate persisted per row by
 * the draft worker. Shape mirrors `ResolvedCard` from the API's
 * cardCatalog route. UI uses it to pre-fill Quick view's identified-
 * match panel without re-querying.
 */
export interface ApiCatalogCandidate {
  id: string;
  name: string;
  number: string;
  setId: string;
  setName: string;
  rarity?: string;
  supertype?: string;
  subtypes?: string[];
  typeLine?: string;
  hp?: string;
  artist?: string;
  language?: string;
  finishes?: string[];
  releasedAt?: string;
  types?: string[];
  pokedexNumbers?: number[];
  series?: string;
  mtgManaCost?: string;
  mtgPowerToughness?: string;
  mtgLoyalty?: string;
  mtgOracleText?: string;
  mtgColors?: string[];
  imageSmall?: string;
  imageLarge?: string;
}

export type CatalogMatchTier = "exact" | "multiple" | "none" | "manual";

export interface ApiImportDraftItem {
  id: string;
  draftId: string;
  csvRow: Record<string, string>;
  parsed: Record<string, unknown>;
  enrichmentStatus: string;
  lookupError?: string;
  imageFrontRef?: string;
  imageBackRef?: string;
  imageFrontSourceUrl?: string;
  imageBackSourceUrl?: string;
  /** TNK-141 phase C — worker-resolved candidate; null when game isn't
   *  wired, when parsed is too thin, or when upstream returned empty. */
  catalog?: ApiCatalogCandidate | null;
  /** TNK-141 phase C — match tier; null on rows enriched before this
   *  phase deployed (treat as "none"). */
  catalogMatch?: CatalogMatchTier | null;
  createdAt: string;
  updatedAt: string;
}

export interface DraftCounters {
  total: number;
  pending: number;
  enriching: number;
  done: number;
  failed: number;
  fixed: number;
}

export interface DraftState {
  draft: ApiImportDraft;
  items: ApiImportDraftItem[];
  counters: DraftCounters;
}

export interface NewDraftItem {
  csvRow: Record<string, string>;
  parsed: Record<string, unknown>;
}

export interface NewDraftPayload {
  sourceFormat: string;
  sourceFilename?: string;
  items: NewDraftItem[];
}

async function callJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? `${init?.method ?? "GET"} ${url} → ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export async function createImportDraft(
  payload: NewDraftPayload,
): Promise<{ draft: ApiImportDraft; counters: DraftCounters }> {
  return callJson("/api/imports", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function getImportDraft(id: string): Promise<DraftState> {
  return callJson(`/api/imports/${encodeURIComponent(id)}`);
}

export async function listImportDrafts(opts?: {
  includeAll?: boolean;
}): Promise<ApiImportDraft[]> {
  const qs = opts?.includeAll ? "?include=all" : "";
  return callJson(`/api/imports${qs}`);
}

export async function patchImportItem(
  draftId: string,
  itemId: string,
  parsed: Record<string, unknown>,
): Promise<ApiImportDraftItem> {
  return callJson(
    `/api/imports/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parsed }),
    },
  );
}

export async function relookupImportItem(
  draftId: string,
  itemId: string,
): Promise<ApiImportDraftItem> {
  return callJson(
    `/api/imports/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}/relookup`,
    { method: "POST" },
  );
}

export async function confirmImportDraft(
  id: string,
  body: { binId: string | null; lotId: string | null },
): Promise<{ draft: ApiImportDraft; cardIds: string[]; cards: number }> {
  return callJson(`/api/imports/${encodeURIComponent(id)}/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteImportDraft(id: string): Promise<void> {
  await callJson(`/api/imports/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/* ----------------------------- TNK-134 -------------------------------- */

export async function deleteImportItem(
  draftId: string,
  itemId: string,
): Promise<{
  deleted: true;
  imagesUnlinked: number;
  counters: DraftCounters;
  totalRows: number;
}> {
  return callJson(
    `/api/imports/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  );
}

export async function swapImportItemImages(
  draftId: string,
  itemId: string,
): Promise<ApiImportDraftItem> {
  return callJson(
    `/api/imports/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}/swap-images`,
    { method: "POST" },
  );
}

export async function swapAllImportItemImages(
  draftId: string,
): Promise<{ swapped: number }> {
  return callJson(
    `/api/imports/${encodeURIComponent(draftId)}/swap-images-all`,
    { method: "POST" },
  );
}

/* ---------------------------- TNK-140 phase D ----------------------------- */

/**
 * Replace a draft item's parsed shape with the catalog row identified
 * by `catalogId`. Server re-queues enrichment so the new image lands
 * in `imageFrontRef` after a beat. Returns the updated item.
 */
export async function overrideImportItem(
  draftId: string,
  itemId: string,
  catalogId: string,
): Promise<ApiImportDraftItem> {
  return callJson(
    `/api/imports/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}/override`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ catalogId }),
    },
  );
}

/* ----------------------------- TNK-133 -------------------------------- */

export interface ApiImportCard {
  id: string;
  name: string;
  setName: string;
  cardNumber: string;
  condition: string;
  quantity: number;
  marketPrice: number;
  listingStatus: string;
  packagedProductId: string | null;
  ebayListingId: string | null;
  ebayListingUrl: string | null;
  archivedAt: string | null;
}

export interface ApiBlockedCard {
  cardId: string;
  reasons: Array<"reservation" | "pick-list" | "sale" | "product">;
}

export interface ImportCardsResponse {
  draftId: string;
  total: number;
  cards: ApiImportCard[];
}

export interface RollbackResponse {
  deleted: number;
  blockers: ApiBlockedCard[];
  /** TNK-133 phase D — when the rollback succeeded (deleted > 0), the
   *  API returns the timestamp it stamped on the draft. Lets the wizard
   *  flip its local draft state to the rolled-back view without a refetch. */
  rolledBackAt?: string;
}

/** Thrown when the rollback endpoint refuses with 409 + a blocker list.
 *  The wizard catches this to render a per-reason breakdown instead of
 *  the generic error banner. */
export class RollbackBlockedError extends Error {
  blockers: ApiBlockedCard[];
  rollbackableCount: number;
  constructor(
    message: string,
    blockers: ApiBlockedCard[],
    rollbackableCount: number,
  ) {
    super(message);
    this.name = "RollbackBlockedError";
    this.blockers = blockers;
    this.rollbackableCount = rollbackableCount;
  }
}

export async function getImportCards(
  draftId: string,
): Promise<ImportCardsResponse> {
  return callJson(`/api/imports/${encodeURIComponent(draftId)}/cards`);
}

export async function rollbackImportDraft(
  draftId: string,
): Promise<RollbackResponse> {
  const res = await apiFetch(
    `/api/imports/${encodeURIComponent(draftId)}/rollback`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirm: true }),
    },
  );
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      blockers?: ApiBlockedCard[];
      rollbackableCount?: number;
    };
    throw new RollbackBlockedError(
      body.error ?? "Some cards are still in use.",
      body.blockers ?? [],
      body.rollbackableCount ?? 0,
    );
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `POST rollback → ${res.status}`);
  }
  return (await res.json()) as RollbackResponse;
}
